import { BY_ID, ROSTER, MODES, DT } from './roster.js';
import { clamp, dist, normalize, random, segmentCircle, segmentBox } from './math.js';
import { makeMap, moveActor, canStand, spawnPoint, inBush, lineOfSight, pathfind } from './map.js';

const idle = () => ({ x:0,z:0,ax:0,az:-1,fire:false,super:false,gadget:false,aim:1,seq:-1 });
const round = n => Math.round(n*1000)/1000;
const BOT_NAMES = ['Frostbyte','Jingle Danger','Snow Mercy','Bad Tidings','Sleigh What','Chill Bill'];

/** Pure, DOM-free simulation. The exact same rules run offline and in a Durable Object. */
export class Game {
  constructor({mode='gift',seed=2026,difficulty='normal'}={}) {
    this.mode=MODES[mode]?mode:'gift'; this.seed=seed; this.rng=random(seed);
    this.map=makeMap(this.mode); this.players=[]; this.projectiles=[]; this.areas=[];
    this.summons=[]; this.pickups=[]; this.events=[]; this.phase='lobby';
    this.t=0; this.tick=0; this.elapsed=0; this.remaining=MODES[this.mode].duration;
    this.countdown=3; this.winner=null; this.holdTeam=-1; this.holdTime=15;
    this.uid=0; this.giftTimer=2.5; this.stormRadius=22; this.difficulty=difficulty;
  }
  id(prefix){return `${prefix}${++this.uid}`;}
  event(type, data={}) { this.events.push({id:this.id('e'),tick:this.tick,type,...data}); }
  addPlayer(id,character='santa',name='Snow Rookie',bot=false) {
    if(this.players.some(p=>p.id===id)) return this.players.find(p=>p.id===id);
    if(this.players.length>=MODES[this.mode].players) return null;
    const used=new Set(this.players.map(p=>p.slot)); let slot=0; while(used.has(slot))slot++;
    const c=BY_ID[character]||ROSTER[0], spawn=spawnPoint(slot,this.mode);
    const p={id,character:c.id,name:String(name).slice(0,18),bot,slot,team:this.mode==='showdown'?slot:slot%2,
      ...spawn,radius:c.radius,hp:c.hp,maxHp:c.hp,ammo:3,reload:0,super:0,gadgets:3,gadgetCd:0,
      cooldown:0,respawn:0,invuln:0,shield:0,haste:0,overdrive:0,invisible:0,slow:0,slowTime:0,
      stun:0,burn:0,burnTime:0,burnOwner:null,lastAction:-10,lastDamage:-10,kills:0,deaths:0,
      gems:0,power:0,ax:0,az:spawn.z>0?-1:1,vx:0,vz:0,y:0,seq:-1,ready:bot,input:idle(),
      _think:0,_pathTime:0,_path:[],_dash:null,_leap:null};
    this.players.push(p); return p;
  }
  select(id,character) {
    if(this.phase!=='lobby'||!BY_ID[character])return false;
    const p=this.players.find(p=>p.id===id); if(!p)return false;
    p.character=character;p.radius=BY_ID[character].radius;p.hp=p.maxHp=BY_ID[character].hp;
    return true;
  }
  setInput(id,input) {
    const p=this.players.find(p=>p.id===id); if(!p||input.seq<=p.seq)return;
    p.seq=input.seq;
    // Keep edge-triggered commands until the next simulation tick.
    p.input={...input,super:input.super||p.input.super,gadget:input.gadget||p.input.gadget};
  }
  start(fillBots=true) {
    if(this.phase!=='lobby')return;
    if(fillBots) {
      const used=new Set(this.players.map(p=>p.character));
      while(this.players.length<MODES[this.mode].players) {
        const options=ROSTER.filter(c=>!used.has(c.id));
        const c=options[Math.floor(this.rng()*options.length)]||ROSTER[0]; used.add(c.id);
        this.addPlayer(this.id('bot'),c.id,BOT_NAMES[this.players.length%BOT_NAMES.length],true);
      }
    }
    if(this.players.length<2)return;
    this.phase='countdown';this.countdown=3;this.event('start');
  }
  enemy(a,b){return a.team!==b.team;}
  canSee(viewer,other) {
    if(!viewer||!this.enemy(viewer,other)||other.hp<=0||viewer.hp<=0&&this.mode==='showdown')return true;
    const scouts=this.players.filter(p=>p.hp>0&&p.team===viewer.team);
    if(other.invisible>0) return scouts.some(p=>dist(p,other)<2.5);
    if(!inBush(other,this.map)||this.t-other.lastAction<1.4||this.t-other.lastDamage<1.2)return true;
    return scouts.some(p=>dist(p,other)<3.1);
  }
  step(dt=DT) {
    dt=clamp(dt,0,.1);if(this.phase==='lobby'||this.phase==='ended')return;
    this.tick++; this.t+=dt;
    if(this.phase==='countdown') {
      this.countdown-=dt;
      if(this.countdown<=0){this.phase='playing';for(const p of this.players)p.invuln=1.5;this.event('go');}
      return;
    }
    this.elapsed+=dt;this.remaining=Math.max(0,MODES[this.mode].duration-this.elapsed);
    for(const p of this.players) this.stepPlayer(p,dt);
    this.stepProjectiles(dt);this.stepAreas(dt);this.stepSummons(dt);this.stepPickups(dt);
    this.checkWin(dt);
    this.events=this.events.filter(e=>this.tick-e.tick<75).slice(-160);
  }
  stepPlayer(p,dt) {
    if(p.hp<=0) {
      if(this.mode!=='showdown' && (p.respawn-=dt)<=0) {
        Object.assign(p,spawnPoint(p.slot,this.mode));p.hp=p.maxHp;p.ammo=3;p.reload=0;p.invuln=2;
        p.burnTime=0;p.stun=0;p.slowTime=0;p._dash=null;p._leap=null;p.y=0;
        this.event('respawn',{x:p.x,z:p.z,target:p.id,color:BY_ID[p.character].color});
      }
      return;
    }
    const c=BY_ID[p.character];
    for(const k of ['cooldown','gadgetCd','invuln','shield','haste','overdrive','invisible','slowTime','stun'])p[k]=Math.max(0,p[k]-dt);
    if(p.burnTime>0) {p.burnTime-=dt;this.damage(p,p.burn*dt,p.burnOwner,'burn');if(p.hp<=0)return;}
    if(p.ammo<3) {
      p.reload+=dt*(p.overdrive>0?1.85:1);
      if(p.reload>=c.reload){p.reload-=c.reload;p.ammo=Math.min(3,p.ammo+1);}
    }else p.reload=0;
    if(this.t-Math.max(p.lastAction,p.lastDamage)>3.2)p.hp=Math.min(p.maxHp,p.hp+p.maxHp*.13*dt);
    if(p.bot) {
      p._think-=dt;p._pathTime-=dt;
      if(p._think<=0){p.input=this.botInput(p);p._think=this.difficulty==='hard'?.10:.18;}
    }
    const input=p.input;
    if(p.stun>0){p.vx=p.vz=0;input.super=input.gadget=false;return;}
    p.ax=input.ax;p.az=input.az;
    if(p._leap) {
      const l=p._leap;l.age+=dt;const k=clamp(l.age/l.duration,0,1);
      p.x=l.sx+(l.x-l.sx)*k;p.z=l.sz+(l.z-l.sz)*k;p.y=Math.sin(k*Math.PI)*3.7;
      if(k>=1){p._leap=null;p.y=0;this.blast(p.x,p.z,3.2,2400,p.id,{destroy:true,stun:1.25,kind:'ice'});}
      return;
    }
    if(p._dash) {
      const d=p._dash,ox=p.x,oz=p.z;d.time-=dt;
      if(d.destroy)this.breakCover(p.x+d.x*d.speed*dt,p.z+d.z*d.speed*dt,1.2);
      moveActor(p,d.x*d.speed*dt,d.z*d.speed*dt,this.map);
      for(const e of this.players)if(e.hp>0&&this.enemy(p,e)&&!d.hit.has(e.id)&&segmentCircle(ox,oz,p.x,p.z,e.x,e.z,e.radius+.75)!==Infinity){
        this.damage(e,d.damage,p.id,'super');d.hit.add(e.id);moveActor(e,d.x*1.4,d.z*1.4,this.map);
      }
      if(d.trail && (!d.last||dist(p,d.last)>1)) {d.last={x:p.x,z:p.z};this.area(p.x,p.z,1.25,4,'fire',p.id,{dps:620});}
      p.vx=d.x*d.speed;p.vz=d.z*d.speed;
      if(d.time<=0)p._dash=null;
      return;
    }
    const multiplier=(p.slowTime>0?1-p.slow:1)*(p.haste>0?1.4:1)*(p.overdrive>0?1.22:1);
    const speed=c.speed*multiplier,ox=p.x,oz=p.z;
    moveActor(p,input.x*speed*dt,input.z*speed*dt,this.map);
    p.vx=(p.x-ox)/dt||0;p.vz=(p.z-oz)/dt||0;
    if(input.super && p.super>=.999)this.useSuper(p);
    if(input.gadget && p.gadgets>0&&p.gadgetCd<=0)this.useGadget(p);
    if(input.fire && p.ammo>=1 && p.cooldown<=0 && !p._dash && !p._leap)this.attack(p);
    input.super=input.gadget=false;
  }
  attack(p) {
    const c=BY_ID[p.character];p.ammo--;p.cooldown=c.cooldown/(p.overdrive>0?1.6:1);
    p.lastAction=this.t;p.invisible=0;
    const damage=c.damage*(1+p.power*.10);
    this.event('shot',{source:p.id,x:p.x,z:p.z,ax:p.ax,az:p.az,color:c.color,kind:c.attack});
    if(c.attack==='melee') {
      for(const e of this.players) {
        const d=dist(p,e),n=normalize(e.x-p.x,e.z-p.z);
        if(e.hp>0&&this.enemy(p,e)&&d<c.range+e.radius&&(n.x*p.ax+n.z*p.az)>Math.cos(c.spread)&&lineOfSight(p,e,this.map))this.damage(e,damage,p.id,'melee');
      }
      for(const w of this.map.walls)if(w.kind==='crate'&&w.hp>0&&dist(p,w)<c.range+1)this.damageWall(w,damage);
      this.event('slash',{source:p.id,x:p.x,z:p.z,ax:p.ax,az:p.az,r:c.range,color:c.color});
      return;
    }
    for(let i=0;i<c.pellets;i++) {
      const angle=Math.atan2(p.ax,p.az)+(i-(c.pellets-1)/2)*(c.spread/Math.max(1,c.pellets-1));
      const ax=Math.sin(angle),az=Math.cos(angle),range=c.range*(c.attack==='lob'?p.input.aim:1);
      this.projectiles.push({id:this.id('p'),owner:p.id,team:p.team,x:p.x+ax*.65,z:p.z+az*.65,
        sx:p.x+ax*.65,sz:p.z+az*.65,vx:ax*c.projectileSpeed,vz:az*c.projectileSpeed,
        life:range/c.projectileSpeed,age:0,total:range/c.projectileSpeed,r:.19,damage,
        color:c.color,kind:c.attack,splash:c.splash||0,burn:c.burn||0,slow:c.slow||0,
        heal:c.heal||0,pierce:c.pierce||1,chain:c.chain||0,hit:[]});
    }
  }
  damageWall(w,damage) {
    if(w.hp<=0)return;w.hp-=damage;
    if(w.hp<=0){w.hp=0;this.event('break',{x:w.x,z:w.z,w:w.w,h:w.h});if(w.kind==='crate')this.pickups.push({id:this.id('cube'),x:w.x,z:w.z,kind:'power',age:0});}
  }
  breakCover(x,z,r) {
    for(const w of this.map.walls)if(w.hp>0&&Math.hypot(x-clamp(x,w.x-w.w/2,w.x+w.w/2),z-clamp(z,w.z-w.h/2,w.z+w.h/2))<r)this.damageWall(w,99999);
  }
  damage(target,amount,ownerId,kind='hit') {
    if(target.hp<=0 || target.invuln>0 || target._leap)return;
    const owner=this.players.find(p=>p.id===ownerId);
    amount=Math.max(0,amount)*(target.shield>0?.45:1);
    const actual=Math.min(amount,target.hp);target.hp=Math.max(0,target.hp-amount);target.lastDamage=this.t;
    if(owner&&owner.id!==target.id)owner.super=clamp(owner.super+actual/BY_ID[owner.character].chargeDamage,0,1);
    if(target.character)target.super=clamp(target.super+actual*.12/BY_ID[target.character].chargeDamage,0,1);
    if(amount>10)this.event('hit',{target:target.id,source:ownerId,x:target.x,z:target.z,amount:Math.round(actual),kind});
    if(target.hp<=0&&target.character)this.kill(target,owner);
  }
  kill(p,killer) {
    p.deaths++;p.respawn=3;p.burnTime=0;p._dash=null;p._leap=null;p.y=0;p.vx=p.vz=0;
    if(killer&&killer.id!==p.id)killer.kills++;
    const count=this.mode==='showdown'?Math.max(1,Math.min(4,p.power+1)):p.gems;
    for(let i=0;i<count;i++){
      const a=this.rng()*Math.PI*2,r=.3+this.rng()*.8;
      this.pickups.push({id:this.id('drop'),x:clamp(p.x+Math.cos(a)*r,-15,15),z:clamp(p.z+Math.sin(a)*r,-13,13),kind:this.mode==='showdown'?'power':'gift',age:0});
    }
    p.gems=0;
    this.event('ko',{target:p.id,source:killer?.id||null,x:p.x,z:p.z,color:BY_ID[p.character].color});
  }
  blast(x,z,r,damage,ownerId,options={}) {
    const owner=this.players.find(p=>p.id===ownerId);if(!owner)return;
    for(const e of this.players)if(e.hp>0&&this.enemy(owner,e)&&dist(e,{x,z})<r+e.radius) {
      // Basic projectiles already include their owner's power when fired.
      this.damage(e,damage*(options.powerScaled?1:1+owner.power*.1),ownerId,'blast');
      if(options.stun)e.stun=Math.max(e.stun,options.stun);
      if(options.slow){e.slow=options.slow;e.slowTime=1.4;}
      if(options.burn){e.burn=options.burn;e.burnTime=3;e.burnOwner=ownerId;}
    }
    if(options.destroy)this.breakCover(x,z,r);
    else for(const w of this.map.walls)if(w.kind==='crate'&&w.hp>0&&dist(w,{x,z})<r+1)this.damageWall(w,damage);
    this.event('blast',{x,z,r,color:BY_ID[owner.character].color,kind:options.kind||'gift'});
  }
  stepProjectiles(dt) {
    for(const b of this.projectiles) {
      const ox=b.x,oz=b.z;b.x+=b.vx*dt;b.z+=b.vz*dt;b.age+=dt;b.life-=dt;
      if(b.kind==='lob') {
        if(b.life<=0)this.blast(b.x,b.z,b.splash,b.damage,b.owner,{kind:'gift',powerScaled:true});
        continue;
      }
      let wall=null,wallTime=Infinity;
      for(const w of this.map.walls)if(w.hp>0){const t=segmentBox(ox,oz,b.x,b.z,w,.08);if(t<wallTime){wallTime=t;wall=w;}}
      const targets=[...this.players,...this.summons].filter(p=>p.hp>0&&p.id!==b.owner&&!b.hit.includes(p.id)&&(p.team!==b.team||b.heal>0));
      const hits=targets.map(p=>({p,t:segmentCircle(ox,oz,b.x,b.z,p.x,p.z,(p.radius||.38)+b.r)})).filter(h=>h.t<=wallTime&&h.t!==Infinity).sort((a,c)=>a.t-c.t);
      for(const {p,t} of hits) {
        b.hit.push(p.id);
        if(p.team===b.team) {p.hp=Math.min(p.maxHp,p.hp+b.heal);this.event('heal',{x:p.x,z:p.z,target:p.id,amount:b.heal});}
        else if(b.splash)this.blast(ox+(b.x-ox)*t,oz+(b.z-oz)*t,b.splash,b.damage,b.owner,{slow:b.slow,kind:b.kind,powerScaled:true});
        else {
          this.damage(p,b.damage,b.owner,b.kind);
          if(b.burn){p.burn=b.burn;p.burnTime=3;p.burnOwner=b.owner;}
          if(b.slow){p.slow=b.slow;p.slowTime=1.2;}
          if(b.chain){
            let last=p;
            const rest=this.players.filter(e=>e.hp>0&&e.team!==b.team&&!b.hit.includes(e.id));
            for(let i=0;i<b.chain;i++) {
              rest.sort((a,c)=>dist(a,last)-dist(c,last));const e=rest.shift();if(!e||dist(e,last)>3.6)break;
              this.damage(e,b.damage*.6,b.owner,'electric');b.hit.push(e.id);
              this.event('arc',{x:last.x,z:last.z,tx:e.x,tz:e.z,color:b.color});last=e;
            }
          }
        }
        if(--b.pierce<=0){b.life=-1;break;}
      }
      if(b.life!==-1&&wall){
        if(wall.kind==='crate')this.damageWall(wall,b.damage);
        if(b.destroy)this.breakCover(ox+(b.x-ox)*wallTime,oz+(b.z-oz)*wallTime,1.7);
        if(b.splash)this.blast(ox+(b.x-ox)*wallTime,oz+(b.z-oz)*wallTime,b.splash,b.damage,b.owner,{slow:b.slow,kind:b.kind,powerScaled:true});
        this.event('impact',{x:ox+(b.x-ox)*wallTime,z:oz+(b.z-oz)*wallTime,color:b.color});
        if(!b.destroy)b.life=-1;
      }
      if(b.life<=0&&b.life!==-1&&b.splash)this.blast(b.x,b.z,b.splash,b.damage,b.owner,{slow:b.slow,kind:b.kind,powerScaled:true});
    }
    this.projectiles=this.projectiles.filter(b=>b.life>0&&Math.abs(b.x)<18&&Math.abs(b.z)<16);
  }
  area(x,z,r,life,kind,owner,extra={}) {
    this.areas.push({id:this.id('a'),x,z,r,life,age:0,kind,owner,delay:0,pulse:0,hit:[],...extra});
  }
  stepAreas(dt) {
    for(const a of this.areas) {
      a.age+=dt;a.life-=dt;a.pulse-=dt;
      if(a.age<a.delay)continue;
      const owner=this.players.find(p=>p.id===a.owner);if(!owner){a.life=-1;continue;}
      if(a.kind==='rain'){this.blast(a.x,a.z,a.r,1250,a.owner,{destroy:true,kind:'gift'});a.life=-1;continue;}
      if(a.kind==='mine') {
        if(this.players.some(p=>p.hp>0&&this.enemy(owner,p)&&dist(p,a)<a.r)){this.blast(a.x,a.z,a.r+1,1750,a.owner,{kind:'gift'});a.life=-1;}
        continue;
      }
      if(a.pulse>0)continue;a.pulse=.5;
      for(const p of this.players)if(p.hp>0&&dist(p,a)<a.r+p.radius) {
        if(this.enemy(owner,p)) {
          if(a.dps)this.damage(p,a.dps*.5,a.owner,a.kind);
          if(a.slow){p.slow=a.slow;p.slowTime=.8;}
          if(a.root&&!a.hit.includes(p.id)){p.stun=1.5;a.hit.push(p.id);}
        }else if(a.heal){p.hp=Math.min(p.maxHp,p.hp+a.heal*.5);this.event('heal',{target:p.id,x:p.x,z:p.z,amount:Math.round(a.heal*.5)});}
      }
    }
    this.areas=this.areas.filter(a=>a.life>0);
  }
  aimPoint(p,range) {
    const distance=range*(p.input.aim||1),target={x:clamp(p.x+p.ax*distance,-14.7,14.7),z:clamp(p.z+p.az*distance,-12.7,12.7)};
    return target;
  }
  landingPoint(p,range) {
    const target=this.aimPoint(p,range);
    for(let k=1;k>=0;k-=.04){const x=p.x+(target.x-p.x)*k,z=p.z+(target.z-p.z)*k;if(canStand(x,z,p.radius,this.map))return{x,z};}
    return{x:p.x,z:p.z};
  }
  useSuper(p) {
    const c=BY_ID[p.character];p.super=0;p.lastAction=this.t;p.invisible=0;
    this.event('super',{source:p.id,x:p.x,z:p.z,color:c.color,name:c.superName});
    const target=this.aimPoint(p,7),dash=(range,damage,destroy=false,trail=false)=>p._dash={x:p.ax,z:p.az,speed:range/.36,time:.36,damage,hit:new Set(),destroy,trail};
    switch(c.super) {
      case 'barrage':
        for(let i=0;i<8;i++){const angle=i*2.4,r=i===0?0:1.2+(i%3)*.6;this.area(target.x+Math.cos(angle)*r,target.z+Math.sin(angle)*r,1.7,2.2,'rain',p.id,{delay:.5+i*.12});}break;
      case 'hearth': this.area(p.x,p.z,3.9,6,'hearth',p.id,{heal:850});break;
      case 'minefield': for(let i=0;i<6;i++){const a=i*Math.PI/3;this.area(target.x+Math.cos(a)*2.5,target.z+Math.sin(a)*2.5,1.1,15,'mine',p.id,{delay:.8});}break;
      case 'hook':dash(7.5,2300);break;
      case 'overdrive':p.overdrive=5.5;p.haste=2;break;
      case 'storm':this.area(target.x,target.z,4,4.5,'electric',p.id,{dps:780,slow:.35});break;
      case 'leap':{const q=this.landingPoint(p,7.5);p._leap={sx:p.x,sz:p.z,x:q.x,z:q.z,age:0,duration:.7};break;}
      case 'blizzard':this.area(p.x,p.z,5.2,6,'blizzard',p.id,{dps:430,slow:.5});p.shield=3;break;
      case 'firetrail':dash(8,1900,true,true);break;
      case 'blink':{const q=this.landingPoint(p,8);this.event('blink',{x:p.x,z:p.z,tx:q.x,tz:q.z,color:c.color});p.x=q.x;p.z=q.z;p.invisible=3.2;break;}
      case 'wave':this.projectiles.push({id:this.id('p'),owner:p.id,team:p.team,x:p.x,z:p.z,sx:p.x,sz:p.z,vx:p.ax*19,vz:p.az*19,life:.75,total:.75,age:0,r:1.1,damage:2850,color:c.color,kind:'wave',pierce:12,hit:[],destroy:true,slow:.55});break;
      case 'stampede':for(let i=0;i<3;i++)this.summons.push({id:this.id('s'),owner:p.id,team:p.team,x:p.x+(i-1)*.8,z:p.z,radius:.35,hp:1700,maxHp:1700,kind:'bird',life:10,cooldown:0});break;
      case 'charge':dash(9.5,2600,true);break;
      case 'turret':{const q=this.landingPoint(p,2);this.summons.push({id:this.id('s'),owner:p.id,team:p.team,...q,radius:.45,hp:3400,maxHp:3400,kind:'turret',life:16,cooldown:0});break;}
      case 'freeze':this.blast(p.x,p.z,4.8,1300,p.id,{stun:1.9,kind:'ice'});break;
      case 'roots':this.area(p.x,p.z,4.6,7,'roots',p.id,{dps:320,heal:400,root:true,slow:.2});break;
    }
  }
  useGadget(p) {
    const c=BY_ID[p.character];p.gadgets--;p.gadgetCd=8;
    if(c.gadget==='reload'){p.ammo=3;p.reload=0;}
    if(c.gadget==='heal')p.hp=Math.min(p.maxHp,p.hp+p.maxHp*.30);
    if(c.gadget==='shield')p.shield=3;
    if(c.gadget==='haste')p.haste=4;
    this.event('gadget',{source:p.id,x:p.x,z:p.z,color:c.color,kind:c.gadget});
  }
  stepSummons(dt) {
    for(const s of this.summons) {
      s.life-=dt;s.cooldown-=dt;if(s.hp<=0)continue;
      const targets=this.players.filter(p=>p.hp>0&&p.team!==s.team&&lineOfSight(s,p,this.map)).sort((a,b)=>dist(a,s)-dist(b,s));
      const target=targets[0];if(!target)continue;
      const d=dist(s,target),n=normalize(target.x-s.x,target.z-s.z);s.ax=n.x;s.az=n.z;
      if(s.kind==='bird'&&d>1)moveActor(s,n.x*6*dt,n.z*6*dt,this.map);
      if(s.cooldown<=0&&d<(s.kind==='bird'?1.5:9)) {
        s.cooldown=s.kind==='bird'?.7:.65;
        if(s.kind==='bird')this.damage(target,550,s.owner,'peck');
        else this.projectiles.push({id:this.id('p'),owner:s.owner,team:s.team,x:s.x,z:s.z,sx:s.x,sz:s.z,vx:n.x*19,vz:n.z*19,life:.5,total:.5,age:0,r:.15,damage:530,color:'#ffda76',kind:'star',pierce:1,hit:[]});
      }
    }
    this.summons=this.summons.filter(s=>s.life>0&&s.hp>0);
  }
  stepPickups(dt) {
    if(this.mode==='gift') {
      this.giftTimer-=dt;
      if(this.giftTimer<=0&&this.pickups.length<24){this.giftTimer=4;const a=this.rng()*Math.PI*2;this.pickups.push({id:this.id('gift'),x:Math.cos(a)*.8,z:Math.sin(a)*.8,kind:'gift',age:0});this.event('spawn-gift',{x:0,z:0});}
    }
    for(const g of this.pickups) {
      g.age+=dt;if(g.age<.45)continue;
      for(const p of this.players)if(p.hp>0&&!p._leap&&dist(p,g)<1) {
        if(g.kind==='power'){p.power++;p.maxHp+=400;p.hp=Math.min(p.maxHp,p.hp+600);}else p.gems++;
        g.taken=true;this.event('pickup',{source:p.id,x:g.x,z:g.z,kind:g.kind});break;
      }
    }
    this.pickups=this.pickups.filter(g=>!g.taken);
  }
  scores(){return [0,1].map(team=>this.players.filter(p=>p.team===team).reduce((n,p)=>n+(this.mode==='duel'?p.kills:p.gems),0));}
  finish(winner){if(this.phase==='ended')return;this.phase='ended';this.winner=winner;this.event('end',{winner});}
  checkWin(dt) {
    if(this.mode==='showdown') {
      this.stormRadius=this.elapsed<22?22:Math.max(1.2,22-(this.elapsed-22)*.155);
      for(const p of this.players)if(p.hp>0&&Math.hypot(p.x,p.z)>this.stormRadius)this.damage(p,(500+this.elapsed*7)*dt,null,'storm');
      const alive=this.players.filter(p=>p.hp>0);
      if(alive.length<=1&&this.elapsed>1)this.finish(alive[0]?.team??-1);
      if(this.remaining<=0)this.finish(alive.length===1?alive[0].team:-1);
      return;
    }
    const scores=this.scores();
    if(this.mode==='gift') {
      const leader=scores[0]>=10&&scores[0]>scores[1]?0:scores[1]>=10&&scores[1]>scores[0]?1:-1;
      if(leader!==this.holdTeam){this.holdTeam=leader;this.holdTime=15;}
      if(leader>=0){this.holdTime-=dt;if(this.holdTime<=0)this.finish(leader);}
    } else {if(scores[0]>=5)this.finish(0);if(scores[1]>=5)this.finish(1);}
    if(this.remaining<=0)this.finish(scores[0]===scores[1]?-1:scores[0]>scores[1]?0:1);
  }
  botInput(p) {
    const c=BY_ID[p.character],enemies=this.players.filter(e=>e.hp>0&&this.enemy(p,e)&&this.canSee(p,e)).sort((a,b)=>dist(a,p)-dist(b,p));
    const e=enemies[0];let goal=e||{x:0,z:0},distance=e?dist(p,e):99;
    const score=this.scores();
    if(this.mode==='gift') {
      const gift=this.pickups.filter(g=>g.kind==='gift').sort((a,b)=>dist(a,p)-dist(b,p))[0];
      if(score[p.team]>=10&&p.gems>=3)goal=spawnPoint(p.slot,this.mode);
      else if(gift&&(!e||dist(p,gift)<distance||distance>5))goal=gift;
      else if(!e||distance>12)goal={x:0,z:0};
    }
    if(this.mode==='showdown') {
      const cube=this.pickups.filter(g=>g.kind==='power').sort((a,b)=>dist(a,p)-dist(b,p))[0];if(cube&&dist(p,cube)<distance)goal=cube;
      if(Math.hypot(p.x,p.z)>this.stormRadius-2)goal={x:0,z:0};
      if(!e){const crate=this.map.walls.find(w=>w.kind==='crate'&&w.hp>0);if(crate)goal=crate;}
    }
    const los=e&&lineOfSight(p,e,this.map),melee=c.attack==='melee',desired=melee?c.range*.65:c.range*.73;
    if(e&&goal===e&&los) {
      const n=normalize(p.x-e.x,p.z-e.z);
      if(distance<desired*.8||p.hp<p.maxHp*.3)goal={x:p.x+n.x*4,z:p.z+n.z*4};
      else if(distance<desired*1.2) {const side=Math.sin(this.t*1.1+p.slot*2)>0?1:-1;goal={x:p.x+n.z*side*2,z:p.z-n.x*side*2};}
    }
    if(p._pathTime<=0||!p._path.length) {p._path=lineOfSight(p,goal,this.map,.58)?[{x:goal.x,z:goal.z}]:pathfind(p,goal,this.map,p.radius+.04);p._pathTime=.55;}
    while(p._path.length&&dist(p,p._path[0])<.55)p._path.shift();
    const waypoint=p._path[0];let movement=waypoint?normalize(waypoint.x-p.x,waypoint.z-p.z):{x:0,z:0};
    // Dodge threatening projectiles rather than walking straight into every shot.
    const threat=this.projectiles.find(b=>b.team!==p.team&&dist(p,b)<3.5&&segmentCircle(b.x,b.z,b.x+b.vx*.25,b.z+b.vz*.25,p.x,p.z,p.radius+.3)!==Infinity);
    if(threat&&!melee){const n=normalize(-threat.vz,threat.vx);movement=n;}
    let aim={x:p.ax,z:p.az},fire=false,aimLength=1;
    if(e) {
      const lead=distance/c.projectileSpeed*(this.difficulty==='easy'?.2:.65);
      const err=(this.rng()-.5)*(this.difficulty==='hard'?.05:this.difficulty==='easy'?.30:.13);
      const n=normalize(e.x+(e.vx||0)*lead-p.x,e.z+(e.vz||0)*lead-p.z),a=Math.atan2(n.x,n.z)+err;
      aim={x:Math.sin(a),z:Math.cos(a)};aimLength=clamp(distance/c.range,.15,1);
      fire=distance<c.range+(melee?.2:0)&&(los||c.attack==='lob');
    }else if(this.mode==='showdown'&&goal.kind==='crate'&&dist(p,goal)<c.range){aim=normalize(goal.x-p.x,goal.z-p.z);fire=true;aimLength=clamp(dist(p,goal)/c.range,.15,1);}
    return {...idle(),...movement,ax:aim.x,az:aim.z,aim:aimLength,fire,
      super:p.super>=1&&!!e&&distance<Math.max(c.range,7),gadget:p.hp<p.maxHp*.38&&p.gadgets>0};
  }
  snapshot(viewerId) {
    const viewer=this.players.find(p=>p.id===viewerId);
    const visible=this.players.filter(p=>this.phase!=='playing'||this.canSee(viewer,p));
    return {type:'state',mode:this.mode,phase:this.phase,tick:this.tick,t:round(this.t),elapsed:round(this.elapsed),
      remaining:round(this.remaining),countdown:Math.ceil(this.countdown),winner:this.winner,
      holdTeam:this.holdTeam,holdTime:round(this.holdTime),scores:this.scores(),stormRadius:this.stormRadius,
      alive:this.players.filter(p=>p.hp>0).length,map:this.map,
      roster:this.players.map(p=>({id:p.id,name:p.name,character:p.character,team:p.team,bot:p.bot,ready:p.ready,kills:p.kills,deaths:p.deaths,hp:p.hp>0?1:0})),
      players:visible.map(p=>({id:p.id,character:p.character,name:p.name,team:p.team,bot:p.bot,
        x:round(p.x),z:round(p.z),y:round(p.y),vx:round(p.vx),vz:round(p.vz),ax:p.ax,az:p.az,
        radius:p.radius,hp:Math.ceil(p.hp),maxHp:p.maxHp,ammo:p.ammo,reload:round(p.reload),super:round(p.super),
        gadgets:p.gadgets,gadgetCd:round(p.gadgetCd),gems:p.gems,power:p.power,kills:p.kills,deaths:p.deaths,
        respawn:round(p.respawn),invuln:p.invuln,shield:p.shield,stun:p.stun,haste:p.haste,overdrive:p.overdrive,
        invisible:p.invisible,slow:p.slow,slowTime:p.slowTime,motion:p._dash?'dash':p._leap?'leap':null,seq:p.seq,lastAction:p.lastAction})),
      projectiles:this.projectiles.map(({hit,...b})=>({...b})),
      areas:this.areas.map(({hit,...a})=>({...a})),summons:this.summons.map(s=>({...s})),
      pickups:this.pickups.map(g=>({...g})),events:this.events.slice()};
  }
}
